import { PHOTO_MAX_SIDE, PHOTO_THUMB_SIDE, type PublicPhoto } from '@karozik/shared';

/** The thumbnail of a panorama stays reasonable. */
const THUMB_MAX_LONG_SIDE = 1200;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Cette photo ne s’ouvre pas. Essaie une photo JPEG ou PNG.'));
    };
    img.src = url;
  });
}

function scaled(src: HTMLImageElement | HTMLCanvasElement, width: number, height: number, k: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(width * k));
  c.height = Math.max(1, Math.round(height * k));
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

function jpeg(c: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('Impossible de préparer cette photo.'))), 'image/jpeg', quality),
  );
}

/**
 * Shrinks a phone photo (12 MP, 3–5 MB) to a 2048 px JPEG and makes its
 * thumbnail, so the room's 4G carries a fraction of it and the server needs no
 * image library. The browser applies the EXIF orientation when drawing; the
 * re-encoding drops every EXIF field, GPS position included.
 */
export async function preparePhoto(file: File): Promise<{ photo: Blob; thumb: Blob }> {
  const img = await loadImage(file);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const full = scaled(img, w, h, Math.min(1, PHOTO_MAX_SIDE / Math.max(w, h)));
  const small = scaled(
    full,
    full.width,
    full.height,
    Math.min(1, PHOTO_THUMB_SIDE / Math.min(full.width, full.height), THUMB_MAX_LONG_SIDE / Math.max(full.width, full.height)),
  );
  try {
    return { photo: await jpeg(full, 0.85), thumb: await jpeg(small, 0.75) };
  } finally {
    // iOS caps the memory of all canvases together: free them before the next photo.
    full.width = full.height = small.width = small.height = 0;
  }
}

/** XHR rather than fetch: an upload progress bar is worth it on the room's 4G. */
export function uploadPhoto(url: string, photo: Blob, thumb: Blob, onProgress: (ratio: number) => void): Promise<PublicPhoto> {
  const body = new FormData();
  body.append('photo', photo, 'photo.jpg');
  body.append('thumb', thumb, 'thumb.jpg');
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as PublicPhoto);
      else reject(new Error((xhr.response as { error?: string } | null)?.error ?? 'Envoi impossible, réessaie.'));
    };
    xhr.onerror = () => reject(new Error('Connexion perdue pendant l’envoi.'));
    xhr.send(body);
  });
}
